/**
 * sectionQueryBuilder — pure function fuer Phase 10 SectionList.
 *
 * Uebersetzt (sectionId, page, filters, kategorie) -> URLSearchParams-String
 * fuer den /list-Endpoint. Pro Sektion ein anderer tracking_status-Filter.
 *
 * Phase 11: SectionId-Union erweitert um Lieferanten-Sektionen (huck/helo/glawel/sporttore).
 * Lieferanten-Sektionen filtern via orderStatusFilter statt tracking_status.
 *
 * KEIN React/DOM/Fetch — pure function, vollstaendig in Vitest testbar.
 */

import type { FilterState } from '../components/FilterBar';

export type SectionId =
  | 'stuck' | 'carrier' | 'neverShipped' | 'ruecksendung' | 'waiting' | 'manualOnly' | 'done'  // Paket-Tab
  | 'huck' | 'helo' | 'glawel' | 'sporttore' | 'wff'         // Lieferanten-Tab (Phase 11 + 15)
  | 'huckDone' | 'heloDone' | 'glawelDone' | 'sporttoreDone' // Phase 16: Abgeschlossen pro Lieferant
  | 'wffDone'                                                 // 2026-07-27: WFF-Abgeschlossen (manufacturer-basiert)
  | 'followup';                                               // Nachverfolgungs-Tab (Phase 14)

export type VersandKategorie = 'paket' | 'lieferanten' | 'followup';

/** Mapping SectionId → tracking_status[] (Backend-Enum-Werte) */
const SECTION_STATUS_MAP: Record<SectionId, string[]> = {
  // Paket-Tab (bestehend, unveraendert)
  stuck: [],                                  // kein status-Filter (onlyStuck=true uebernimmt)
  carrier: ['carrier'],
  neverShipped: [],                           // kein status-Filter (onlyNeverShipped=true uebernimmt)
  // 29.07.2026: "Paket zurueck zu anstoss" — KEIN status-Filter, denn genau die
  // ABGESCHLOSSENEN Auftraege sind das Problem (Auftrag erledigt, Ware liegt bei uns).
  ruecksendung: [],
  waiting: ['open', 'awaiting_package'],
  manualOnly: ['manual_only'],
  done: ['done', 'closed_manually'],
  // Lieferanten-Tab (kein tracking_status — Filter via orderStatusFilter)
  huck: [],
  helo: [],
  glawel: [],
  sporttore: [],
  // Phase 16: Abgeschlossen pro Lieferant (kein tracking_status — Filter via supplierCompletedFor)
  huckDone: [],
  heloDone: [],
  glawelDone: [],
  sporttoreDone: [],
  // Phase 15: WFF — Filter ueber manufacturer_name=EWFF (im Backend, via dedicated flag)
  wff: [],
  // 2026-07-27: WFF-Abgeschlossen — manufacturerDoneFilter=EWFF (im Backend)
  wffDone: [],
  // Nachverfolgungs-Tab (Phase 14) — alle followup-markierten Orders, ein-section-tab
  followup: [],
};

/**
 * Mapping Lieferanten-Done-SectionId → Plenty-orderStatusId (als String fuer Whitelist-Match).
 * Phase 16: Filter "irgendwann bei diesem Lieferant gewesen, jetzt weiter".
 */
export const SUPPLIER_DONE_SECTION_STATUS_ID: Record<
  'huckDone' | 'heloDone' | 'glawelDone' | 'sporttoreDone',
  string
> = {
  huckDone:      '5.50',
  heloDone:      '5.60',
  glawelDone:    '5.65',
  sporttoreDone: '5.66',
};

/** Type-Guard: prüft ob sectionId eine Lieferanten-Done-Sektion ist. */
export function isSupplierDoneSection(
  sectionId: SectionId,
): sectionId is 'huckDone' | 'heloDone' | 'glawelDone' | 'sporttoreDone' {
  return sectionId === 'huckDone' || sectionId === 'heloDone'
      || sectionId === 'glawelDone' || sectionId === 'sporttoreDone';
}

/**
 * Mapping Lieferanten-SectionId → Plenty-orderStatusId.
 * Phase 11: Huck=5.5, Helo=5.6, GlaWel=5.65, Sporttore=5.66
 */
export const SUPPLIER_SECTION_STATUS_ID: Record<'huck' | 'helo' | 'glawel' | 'sporttore', number> = {
  huck:      5.5,
  helo:      5.6,
  glawel:    5.65,
  sporttore: 5.66,
};

/** Type-Guard: prüft ob sectionId eine Lieferanten-Sektion ist (inkl. WFF Phase 15). */
export function isSupplierSection(sectionId: SectionId): sectionId is 'huck' | 'helo' | 'glawel' | 'sporttore' | 'wff' {
  return sectionId === 'huck' || sectionId === 'helo' || sectionId === 'glawel'
      || sectionId === 'sporttore' || sectionId === 'wff';
}

export interface BuildSectionQueryArgs {
  sectionId: SectionId;
  page: number;
  filters: FilterState;
  kategorie: VersandKategorie;
  pageSize?: number;
  /** 20.08.2026: Rücksendungs-Reiter — true zeigt auch kaufmaennisch geklaerte Faelle. */
  mitGeklaerten?: boolean;
  /** 24.08.2026: Nie-versendet- + Rücksendungs-Reiter — true zeigt auch abgeschlossene Faelle. */
  mitAbgeschlossenen?: boolean;
}

export function buildSectionQuery(args: BuildSectionQueryArgs): string {
  const { sectionId, page, filters, kategorie } = args;
  const pageSize = args.pageSize ?? 50;
  const params = new URLSearchParams();

  // Sektion-spezifischer status-Filter (hat Vorrang vor filters.status)
  // Lieferanten-Sektionen haben leeres Array → kein status-Filter (orderStatusFilter stattdessen)
  const statusList = SECTION_STATUS_MAP[sectionId];
  if (statusList.length > 0) {
    params.set('status', statusList.join(','));
  }
  // stuck: status leer + onlyStuck=true (forciert, ueberschreibt filters.onlyStuck)
  if (sectionId === 'stuck') {
    params.set('onlyStuck', 'true');
    // 24.08.2026: exklusive Reiter — Stuck zeigt nur, was kein Spezial-Reiter
    // (Paket zurueck / Nie versendet) beansprucht. Nur im Paket-Tab.
    if (kategorie === 'paket') params.set('exklusiv', 'stuck');
  } else if (sectionId === 'neverShipped') {
    // Phase 18: "Nie versendet" — Label gedruckt/DHL-Storno, kein Transport-Scan
    params.set('onlyNeverShipped', 'true');
    // 24.08.2026: abgeschlossene Faelle nur auf Wunsch mitladen (Default: ausgeblendet)
    if (args.mitAbgeschlossenen) params.set('mitAbgeschlossenen', 'true');
    if (filters.onlyStuck) params.set('onlyStuck', 'true');
  } else if (sectionId === 'ruecksendung') {
    // 29.07.2026: "Paket zurueck zu anstoss" — DHL-Ruecksendung an uns
    params.set('onlyRuecksendung', 'true');
    // 20.08.2026: geklaerte Faelle nur auf Wunsch mitladen (Default: ausgeblendet)
    if (args.mitGeklaerten) params.set('mitGeklaerten', 'true');
    // 24.08.2026: abgeschlossene Faelle nur auf Wunsch mitladen (Default: ausgeblendet)
    if (args.mitAbgeschlossenen) params.set('mitAbgeschlossenen', 'true');
    if (filters.onlyStuck) params.set('onlyStuck', 'true');
  } else if (filters.onlyStuck) {
    // Nicht-stuck-Sektion + User-Filter onlyStuck=true → kombinieren
    params.set('onlyStuck', 'true');
  }

  // 24.08.2026: exklusive Paket-Reiter — Carrier/Wartet/Manuell blenden aus,
  // was ein hoeher priorisierter Reiter beansprucht (Stuck/Rueck/Nie-versendet).
  // Nur im Paket-Tab; 'done' bleibt bewusst vollstaendig (Archiv-Sicht).
  // Ausnahme: der User-Filter "Nur stuck" hebelt die Exklusivitaet bewusst aus —
  // sonst waere "Beim Carrier + Nur stuck" per Konstruktion immer leer.
  if (kategorie === 'paket' && !params.has('exklusiv') && !filters.onlyStuck) {
    if (sectionId === 'carrier') params.set('exklusiv', 'carrier');
    else if (sectionId === 'waiting') params.set('exklusiv', 'waiting');
    else if (sectionId === 'manualOnly') params.set('exklusiv', 'manualOnly');
  }

  // FilterBar-Filter (ausser status, das die Sektion bestimmt)
  if (filters.hasPackage !== 'all') params.set('hasPackage', filters.hasPackage);
  if (filters.carrier !== 'all') params.set('carrier', filters.carrier);
  if (filters.search.trim()) params.set('search', filters.search.trim());

  // Phase 11: Lieferanten-Sektion → orderStatusFilter setzen.
  // Phase 15: WFF nutzt manufacturerFilter statt orderStatusFilter.
  // Phase 16: Lieferanten-Done nutzt supplierCompletedFor — KEIN orderStatusFilter und KEIN versandKategorie-Lieferanten-Filter
  if (sectionId === 'wff') {
    params.set('manufacturerFilter', 'EWFF');
  } else if (sectionId === 'wffDone') {
    // 2026-07-27: WFF-Abgeschlossen — EWFF + tracking_status IN (done, closed_manually)
    params.set('manufacturerDoneFilter', 'EWFF');
  } else if (isSupplierDoneSection(sectionId)) {
    params.set('supplierCompletedFor', SUPPLIER_DONE_SECTION_STATUS_ID[sectionId]);
  } else if (isSupplierSection(sectionId)) {
    params.set('orderStatusFilter', String(SUPPLIER_SECTION_STATUS_ID[sectionId as 'huck' | 'helo' | 'glawel' | 'sporttore']));
  }

  // Pagination + Tab
  params.set('versandKategorie', kategorie);
  params.set('page', String(page));
  params.set('limit', String(pageSize));

  // Phase 16: Sortier-Option (default 'stuck_first' wird ans Backend nur uebergeben
  // wenn explizit anders gesetzt — spart Query-Param-Rauschen)
  if (filters.sortBy && filters.sortBy !== 'stuck_first') {
    params.set('sortBy', filters.sortBy);
  }

  return params.toString();
}
